// Module-level configuration for `OnlineMenuHost`: transport URLs, the
// storage key and the create-room form. The host holds behaviour; this holds
// the values it reads.
import type { ClockPreset } from '../game/clock.js';
import type { Color } from '../game/types.js';
import { SEATS } from '../network/protocol.js';

/** Dev defaults. Production reads the env vars the combined build derives from `VITE_WS_BASE_URL`. */
const DEV_WS_URL = 'ws://localhost:3001/ws/chess';
const DEV_LOBBY_WS_URL = 'ws://localhost:3001/ws/lobby';

export const SESSION_STORAGE_KEY = 'chess.online.session';

function configured(key: string, fallback: string): string {
  const value = import.meta.env[key] as string | undefined;
  return value !== undefined && value !== '' ? value : fallback;
}

export function resolveServerUrl(): string {
  return configured('VITE_WS_SERVER_URL', DEV_WS_URL);
}

export function resolveLobbyServerUrl(): string {
  return configured('VITE_LOBBY_WS_URL', DEV_LOBBY_WS_URL);
}

/** What the host chooses before opening a room. */
export interface OnlineRoomForm {
  /** The side the creator plays in the first game; a rematch swaps it. */
  readonly color: Color;
  /** Null plays without a clock. */
  readonly clock: ClockPreset | null;
}

export const DEFAULT_ROOM_FORM: OnlineRoomForm = { color: 'w', clock: null };

/** The room's only rule set today; sent so the server validates it. */
export const ROOM_VARIANT = 'fide';

/** `CREATE_ROOM`'s `preferredSeat`: the index of the creator's role in the seat order. */
export function preferredSeatFor(color: Color): number {
  return SEATS.indexOf(color === 'w' ? 'white' : 'black');
}

/** `CREATE_ROOM`'s options bag — the clock preset, read by the server's `roomOptions.ts`. */
export function roomOptionsFor(form: OnlineRoomForm): Record<string, unknown> | undefined {
  return form.clock === null ? undefined : { clock: form.clock };
}
